import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { apiClient } from '../lib/api-client';
import { langAutonym } from '../lib/lang-names';

// ── Types ──

interface CampaignMeta {
  campaign_id: string;
  language_code: string;
  level: string;
  topic: string;
  content_type?: string;
  status: string;
  total_entries: number;
  created_at: string;
  updated_at: string;
}

interface CampaignEntry {
  campaign_id: string;
  entry_key: string;
  week: number;
  dialog_id: string;
  dialog_vertical_id: string;
  dialog_title: string;
  dialog_slug: string;
  scenario_code: string;
  scenario_category: string;
  quality_score: number;
}

interface ProposedEntry {
  week: number;
  dialog_id: string;
  dialog_vertical_id: string;
  dialog_title: string;
  dialog_slug: string;
  scenario_code: string;
  scenario_category: string;
  quality_score: number;
}

interface ProposeResult {
  language: string;
  level: string;
  topic: string;
  total_available: number;
  proposed: ProposedEntry[];
}

interface Division {
  id: string;
  name_english: string;
  slug: string;
  sort_order: number;
}

interface TopicItem {
  id: string;
  division_id: string;
  name_english: string;
  slug: string;
  sort_order: number;
}

interface Subtopic {
  id: string;
  topic_id: string;
  name_english: string;
  sort_order: number;
}

interface HierarchyResponse {
  hierarchy: {
    divisions: Division[];
    topics: TopicItem[];
    subtopics: Subtopic[];
  } | null;
  scenario_counts: Record<string, number>;
  stats: { total_scenarios: number };
}

type ContentTab = 'dialogs' | 'stories' | 'dramas' | 'alphabet';
type View = 'list' | 'detail' | 'create';

// ── Flattened topic option for the picker ──

interface TopicOption {
  value: string;
  label: string;
  group: string;
  depth: number;
  scenarioCount?: number;
}

// ── Constants ──

const TABS: { key: ContentTab; label: string; ready: boolean }[] = [
  { key: 'dialogs', label: 'Dialogs', ready: true },
  { key: 'stories', label: 'Stories', ready: false },
  { key: 'dramas', label: 'Dramas', ready: false },
  { key: 'alphabet', label: 'Alphabet', ready: false },
];

const SCRIPT_LENGTH_GROUPS: Record<string, { label: string; description: string; estimate: string }> = {
  logographic: {
    label: 'Logographic',
    description: 'CJK hanzi/kanji, Tangut, hieroglyphs, cuneiform',
    estimate: '200+ entries',
  },
  abugida: {
    label: 'Abugida / syllabary',
    description: 'Devanagari, Tamil, Thai, Khmer, Burmese, Tibetan, Ethiopic, etc.',
    estimate: '80-120 entries',
  },
  alphabet: {
    label: 'Alphabet / abjad',
    description: 'Cyrillic, Greek, Georgian, Hangul, Hebrew, Arabic, Armenian, etc.',
    estimate: '30-50 entries',
  },
};

const BUILTIN_TOPICS: TopicOption[] = [
  { value: 'general', label: 'General (A1 base + random encounters)', group: 'Built-in', depth: 0 },
  { value: 'holiday', label: 'Holiday', group: 'Built-in', depth: 0 },
];

// ── Topic Picker Component ──

function TopicPicker({
  value,
  onChange,
  options,
  loading,
}: {
  value: string;
  onChange: (v: string) => void;
  options: TopicOption[];
  loading: boolean;
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || o.group.toLowerCase().includes(q)
    );
  }, [options, search]);

  const grouped = useMemo(() => {
    const groups: Record<string, TopicOption[]> = {};
    for (const o of filtered) {
      if (!groups[o.group]) groups[o.group] = [];
      groups[o.group]!.push(o);
    }
    return groups;
  }, [filtered]);

  const selectedLabel = options.find((o) => o.value === value)?.label ?? value;

  return (
    <div ref={ref} className="relative w-full sm:w-80">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-sm text-white text-left truncate"
      >
        {value ? selectedLabel : 'Select topic...'}
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full sm:w-96 bg-zinc-900 border border-zinc-700 rounded shadow-xl max-h-96 flex flex-col">
          <div className="p-2 border-b border-zinc-800">
            <input
              type="text"
              placeholder="Search topics..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full px-3 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-sm text-white"
              autoFocus
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <p className="p-3 text-zinc-500 text-sm">Loading topics...</p>
            ) : filtered.length === 0 ? (
              <p className="p-3 text-zinc-500 text-sm">No topics match "{search}"</p>
            ) : (
              Object.entries(grouped).map(([group, items]) => (
                <div key={group}>
                  <div className="px-3 py-1.5 text-[11px] font-medium uppercase tracking-wider text-zinc-500 bg-zinc-800/50 sticky top-0">
                    {group}
                  </div>
                  {items.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => { onChange(o.value); setOpen(false); setSearch(''); }}
                      className={`w-full text-left px-3 py-1.5 text-sm hover:bg-zinc-800 transition-colors ${
                        value === o.value ? 'text-accent-teal bg-accent-teal/10' : 'text-white'
                      }`}
                      style={{ paddingLeft: `${12 + o.depth * 16}px` }}
                    >
                      {o.label}
                      {o.scenarioCount !== undefined && (
                        <span className="ml-2 text-xs text-zinc-500">{o.scenarioCount} scenarios</span>
                      )}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab Bar ──

function TabBar({ active, onChange }: { active: ContentTab; onChange: (t: ContentTab) => void }) {
  return (
    <div className="flex gap-1 mb-6 border-b border-zinc-700 overflow-x-auto">
      {TABS.map((tab) => (
        <button
          key={tab.key}
          onClick={() => onChange(tab.key)}
          className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
            active === tab.key
              ? 'border-accent-teal text-accent-teal'
              : 'border-transparent text-zinc-400 hover:text-zinc-200'
          }`}
        >
          {tab.label}
          {!tab.ready && <span className="ml-1.5 text-xs text-zinc-600">(Jul)</span>}
        </button>
      ))}
    </div>
  );
}

// ── Placeholder Tabs ──

function PlaceholderTab({ type }: { type: ContentTab }) {
  if (type === 'stories') {
    return (
      <div className="p-6 bg-zinc-800/50 rounded border border-zinc-700/50">
        <h2 className="text-lg font-medium text-white mb-3">Story Campaigns</h2>
        <p className="text-sm text-zinc-400 mb-4">
          Multi-episode story arcs sent as email sequences. Each campaign follows one story from
          start to finish, with episodes ordered by narrative sequence.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div className="p-3 bg-zinc-900 rounded">
            <span className="text-zinc-500">Propose logic</span>
            <p className="text-white mt-1">Episode-ordered per story arc</p>
          </div>
          <div className="p-3 bg-zinc-900 rounded">
            <span className="text-zinc-500">Campaign length</span>
            <p className="text-white mt-1">Variable (per story)</p>
          </div>
          <div className="p-3 bg-zinc-900 rounded">
            <span className="text-zinc-500">Cadence</span>
            <p className="text-white mt-1">Every 84 hours</p>
          </div>
        </div>
        <p className="mt-4 text-xs text-zinc-500">
          Requires story content pipeline (planned July 2026). Same DynamoDB table, separate Lambda.
          Campaign ID prefix: <span className="font-mono text-zinc-400">sc-</span>
        </p>
      </div>
    );
  }

  if (type === 'dramas') {
    return (
      <div className="p-6 bg-zinc-800/50 rounded border border-zinc-700/50">
        <h2 className="text-lg font-medium text-white mb-3">Drama Campaigns</h2>
        <p className="text-sm text-zinc-400 mb-4">
          Multi-episode drama seasons sent as email sequences. Each campaign follows one season,
          episodes ordered by production sequence.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
          <div className="p-3 bg-zinc-900 rounded">
            <span className="text-zinc-500">Propose logic</span>
            <p className="text-white mt-1">Episode-ordered per season</p>
          </div>
          <div className="p-3 bg-zinc-900 rounded">
            <span className="text-zinc-500">Campaign length</span>
            <p className="text-white mt-1">Variable (per season)</p>
          </div>
          <div className="p-3 bg-zinc-900 rounded">
            <span className="text-zinc-500">Cadence</span>
            <p className="text-white mt-1">Every 84 hours</p>
          </div>
        </div>
        <p className="mt-4 text-xs text-zinc-500">
          Requires drama content pipeline (planned July 2026). Same DynamoDB table, separate Lambda.
          Campaign ID prefix: <span className="font-mono text-zinc-400">drc-</span>
        </p>
      </div>
    );
  }

  if (type === 'alphabet') {
    return (
      <div className="p-6 bg-zinc-800/50 rounded border border-zinc-700/50">
        <h2 className="text-lg font-medium text-white mb-3">Alphabet Campaigns</h2>
        <p className="text-sm text-zinc-400 mb-4">
          Writing system courses sent as email sequences. Campaign length varies by script complexity.
          Two variants per script: free users receive 1 email/week, paid users receive 5-7/week.
          Same content, Blitzscale controls the cadence.
        </p>
        <div className="space-y-3 mb-4">
          {Object.entries(SCRIPT_LENGTH_GROUPS).map(([key, group]) => (
            <div key={key} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 bg-zinc-900 rounded text-sm">
              <span className="text-accent-teal font-medium w-40 shrink-0">{group.label}</span>
              <span className="text-zinc-400 flex-1">{group.description}</span>
              <span className="text-white font-mono text-xs">{group.estimate}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-zinc-500">
          Requires alphabet content pipeline (planned July 2026). Same DynamoDB table, separate Lambda.
          Campaign ID prefix: <span className="font-mono text-zinc-400">ac-</span>
          &nbsp;Two campaigns per script: <span className="font-mono text-zinc-400">ac-JPN-kanji-free</span> and <span className="font-mono text-zinc-400">ac-JPN-kanji-paid</span>.
        </p>
      </div>
    );
  }

  return null;
}

// ── Main Component ──

export default function Campaigns() {
  const [activeTab, setActiveTab] = useState<ContentTab>('dialogs');
  const [view, setView] = useState<View>('list');
  const [campaigns, setCampaigns] = useState<CampaignMeta[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [filterLang, setFilterLang] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [selectedCampaign, setSelectedCampaign] = useState<CampaignMeta | null>(null);
  const [entries, setEntries] = useState<CampaignEntry[]>([]);

  const [newLang, setNewLang] = useState('');
  const [newLevel, setNewLevel] = useState('A1');
  const [newTopic, setNewTopic] = useState('general');
  const [proposed, setProposed] = useState<ProposedEntry[] | null>(null);
  const [proposing, setProposing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [proposeResult, setProposeResult] = useState<ProposeResult | null>(null);

  // Topic hierarchy (loaded once)
  const [topicOptions, setTopicOptions] = useState<TopicOption[]>(BUILTIN_TOPICS);
  const [topicsLoading, setTopicsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setTopicsLoading(true);
      try {
        const data = await apiClient<HierarchyResponse>('/api/admin/scenarios');
        if (cancelled || !data.hierarchy) return;
        const { divisions, topics, subtopics } = data.hierarchy;
        const counts = data.scenario_counts ?? {};

        const opts: TopicOption[] = [...BUILTIN_TOPICS];

        const sortedDivs = [...divisions].sort((a, b) => a.sort_order - b.sort_order);
        for (const div of sortedDivs) {
          const divTopics = topics
            .filter((t) => t.division_id === div.id)
            .sort((a, b) => a.sort_order - b.sort_order);

          for (const topic of divTopics) {
            const topicSubs = subtopics
              .filter((s) => s.topic_id === topic.id)
              .sort((a, b) => a.sort_order - b.sort_order);

            // Topic-level option (campaign covers all subtopics under this topic)
            const topicScenarioCount = topicSubs.reduce(
              (sum, s) => sum + (counts[s.id] ?? 0), 0
            );
            opts.push({
              value: `topic:${topic.id}`,
              label: topic.name_english,
              group: div.name_english,
              depth: 0,
              scenarioCount: topicScenarioCount,
            });

            // Subtopic-level options
            for (const sub of topicSubs) {
              opts.push({
                value: `subtopic:${sub.id}`,
                label: sub.name_english,
                group: div.name_english,
                depth: 1,
                scenarioCount: counts[sub.id],
              });
            }
          }
        }

        setTopicOptions(opts);
      } catch {
        // Keep built-in topics on failure
      } finally {
        if (!cancelled) setTopicsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const resetDialogState = useCallback(() => {
    setView('list');
    setProposed(null);
    setProposeResult(null);
    setError('');
    setSelectedCampaign(null);
    setEntries([]);
  }, []);

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filterLang) params.set('language', filterLang);
      if (filterLevel) params.set('level', filterLevel);
      if (filterStatus) params.set('status', filterStatus);
      const qs = params.toString();
      const data = await apiClient<{ campaigns: CampaignMeta[] }>(
        `/api/admin/campaigns${qs ? `?${qs}` : ''}`
      );
      setCampaigns(data.campaigns);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  }, [filterLang, filterLevel, filterStatus]);

  useEffect(() => {
    if (activeTab === 'dialogs' && view === 'list') loadCampaigns();
  }, [activeTab, view, loadCampaigns]);

  const handleTabChange = (tab: ContentTab) => {
    setActiveTab(tab);
    resetDialogState();
  };

  const openDetail = async (campaign: CampaignMeta) => {
    setLoading(true);
    setError('');
    try {
      const data = await apiClient<{ meta: CampaignMeta; entries: CampaignEntry[] }>(
        `/api/admin/campaigns/${encodeURIComponent(campaign.campaign_id)}`
      );
      setSelectedCampaign(data.meta);
      setEntries(data.entries);
      setView('detail');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load campaign');
    } finally {
      setLoading(false);
    }
  };

  const handlePropose = async () => {
    if (!newLang) { setError('Select a language'); return; }
    if (!newTopic) { setError('Select a topic'); return; }
    setProposing(true);
    setError('');
    try {
      const data = await apiClient<ProposeResult>('/api/admin/campaigns/propose', {
        method: 'POST',
        body: JSON.stringify({ language: newLang, level: newLevel, topic: newTopic }),
      });
      setProposeResult(data);
      setProposed(data.proposed);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Propose failed');
    } finally {
      setProposing(false);
    }
  };

  const handleCreate = async () => {
    if (!proposed || proposed.length === 0) return;
    setCreating(true);
    setError('');
    try {
      const data = await apiClient<{ ok: boolean; campaign_id: string }>('/api/admin/campaigns', {
        method: 'POST',
        body: JSON.stringify({
          language: newLang,
          level: newLevel,
          topic: newTopic,
          entries: proposed,
        }),
      });
      if (data.ok) {
        resetDialogState();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setCreating(false);
    }
  };

  const handleActivate = async (campaignId: string) => {
    setError('');
    try {
      await apiClient(`/api/admin/campaigns/${encodeURIComponent(campaignId)}/activate`, {
        method: 'PUT',
      });
      if (selectedCampaign) {
        setSelectedCampaign({ ...selectedCampaign, status: 'active' });
      }
      loadCampaigns();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Activate failed');
    }
  };

  const handleDelete = async (campaignId: string) => {
    if (!confirm('Delete this campaign?')) return;
    setError('');
    try {
      await apiClient(`/api/admin/campaigns/${encodeURIComponent(campaignId)}`, {
        method: 'DELETE',
      });
      resetDialogState();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  const levels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
  const currentTabReady = TABS.find((t) => t.key === activeTab)?.ready ?? false;
  const selectedTopicLabel = topicOptions.find((o) => o.value === newTopic)?.label ?? newTopic;

  return (
    <div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
        <h1 className="text-2xl font-semibold text-white">Campaigns</h1>
        {currentTabReady && view === 'list' && (
          <button
            onClick={() => { setView('create'); setProposed(null); setProposeResult(null); setError(''); }}
            className="px-4 py-2 bg-accent-teal text-black rounded font-medium hover:bg-accent-teal/80"
          >
            New Campaign
          </button>
        )}
        {currentTabReady && view !== 'list' && (
          <button
            onClick={resetDialogState}
            className="px-4 py-2 bg-zinc-700 text-white rounded hover:bg-zinc-600"
          >
            Back to List
          </button>
        )}
      </div>

      <TabBar active={activeTab} onChange={handleTabChange} />

      {error && (
        <div className="mb-4 p-3 bg-red-900/40 border border-red-700 text-red-300 rounded text-sm">
          {error}
        </div>
      )}

      {/* ── Placeholder tabs ── */}
      {!currentTabReady && <PlaceholderTab type={activeTab} />}

      {/* ── Dialogs: List View ── */}
      {activeTab === 'dialogs' && view === 'list' && (
        <>
          <div className="flex flex-wrap gap-3 mb-4">
            <input
              type="text"
              placeholder="Language code (e.g. DEU)"
              value={filterLang}
              onChange={(e) => setFilterLang(e.target.value.toUpperCase())}
              className="w-full sm:w-48 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-sm text-white"
            />
            <select
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value)}
              className="w-full sm:w-32 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-sm text-white"
            >
              <option value="">All Levels</option>
              {levels.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full sm:w-32 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded text-sm text-white"
            >
              <option value="">All Status</option>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
            </select>
            <button
              onClick={loadCampaigns}
              className="px-4 py-2 bg-zinc-700 text-white rounded hover:bg-zinc-600 text-sm"
            >
              Filter
            </button>
          </div>

          {loading ? (
            <p className="text-zinc-500">Loading...</p>
          ) : campaigns.length === 0 ? (
            <p className="text-zinc-500">No campaigns found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[700px] text-sm">
                <thead>
                  <tr className="border-b border-zinc-700 text-left text-zinc-400">
                    <th className="py-2 px-3">Language</th>
                    <th className="py-2 px-3">Level</th>
                    <th className="py-2 px-3">Topic</th>
                    <th className="py-2 px-3">Status</th>
                    <th className="py-2 px-3">Entries</th>
                    <th className="py-2 px-3">Created</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => (
                    <tr
                      key={c.campaign_id}
                      onClick={() => openDetail(c)}
                      className="border-b border-zinc-800 hover:bg-zinc-800/50 cursor-pointer"
                    >
                      <td className="py-2 px-3 text-white">
                        {langAutonym(c.language_code)} <span className="text-zinc-500">({c.language_code})</span>
                      </td>
                      <td className="py-2 px-3 text-white">{c.level}</td>
                      <td className="py-2 px-3 text-white">{c.topic}</td>
                      <td className="py-2 px-3">
                        <span className={c.status === 'active' ? 'text-green-400' : 'text-yellow-400'}>
                          {c.status}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-white">{c.total_entries}</td>
                      <td className="py-2 px-3 text-zinc-400">{c.created_at?.slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── Dialogs: Detail View ── */}
      {activeTab === 'dialogs' && view === 'detail' && selectedCampaign && (
        <>
          <div className="mb-4 p-4 bg-zinc-800 rounded">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-zinc-400">Language:</span>{' '}
                <span className="text-white">{langAutonym(selectedCampaign.language_code)} ({selectedCampaign.language_code})</span>
              </div>
              <div>
                <span className="text-zinc-400">Level:</span>{' '}
                <span className="text-white">{selectedCampaign.level}</span>
              </div>
              <div>
                <span className="text-zinc-400">Topic:</span>{' '}
                <span className="text-white">{selectedCampaign.topic}</span>
              </div>
              <div>
                <span className="text-zinc-400">Status:</span>{' '}
                <span className={selectedCampaign.status === 'active' ? 'text-green-400' : 'text-yellow-400'}>
                  {selectedCampaign.status}
                </span>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {selectedCampaign.status === 'draft' && (
                <>
                  <button
                    onClick={() => handleActivate(selectedCampaign.campaign_id)}
                    className="px-3 py-1.5 bg-green-700 text-white rounded text-sm hover:bg-green-600"
                  >
                    Activate
                  </button>
                  <button
                    onClick={() => handleDelete(selectedCampaign.campaign_id)}
                    className="px-3 py-1.5 bg-red-800 text-white rounded text-sm hover:bg-red-700"
                  >
                    Delete
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] text-sm">
              <thead>
                <tr className="border-b border-zinc-700 text-left text-zinc-400">
                  <th className="py-2 px-3">Week</th>
                  <th className="py-2 px-3">Title</th>
                  <th className="py-2 px-3">Scenario</th>
                  <th className="py-2 px-3">Category</th>
                  <th className="py-2 px-3">Quality</th>
                  <th className="py-2 px-3">Link</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.entry_key} className="border-b border-zinc-800">
                    <td className="py-2 px-3 text-white font-mono">{e.entry_key}</td>
                    <td className="py-2 px-3 text-white">{e.dialog_title}</td>
                    <td className="py-2 px-3 text-zinc-400">{e.scenario_code}</td>
                    <td className="py-2 px-3 text-zinc-400">{e.scenario_category}</td>
                    <td className="py-2 px-3 text-white">{e.quality_score}</td>
                    <td className="py-2 px-3">
                      {e.dialog_slug && (
                        <a
                          href={`https://dialogs.glossika.com/${e.dialog_slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-accent-teal hover:underline"
                        >
                          View
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Dialogs: Create View ── */}
      {activeTab === 'dialogs' && view === 'create' && (
        <>
          <div className="p-4 bg-zinc-800 rounded mb-4">
            <h2 className="text-lg font-medium text-white mb-3">Create Dialog Campaign</h2>
            <div className="flex flex-wrap gap-3 items-end">
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Language Code</label>
                <input
                  type="text"
                  placeholder="e.g. DEU"
                  value={newLang}
                  onChange={(e) => setNewLang(e.target.value.toUpperCase())}
                  className="w-full sm:w-32 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-sm text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Level</label>
                <select
                  value={newLevel}
                  onChange={(e) => setNewLevel(e.target.value)}
                  className="w-full sm:w-24 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded text-sm text-white"
                >
                  {levels.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs text-zinc-400 mb-1">Topic</label>
                <TopicPicker
                  value={newTopic}
                  onChange={setNewTopic}
                  options={topicOptions}
                  loading={topicsLoading}
                />
              </div>
            </div>
            <div className="mt-3">
              <button
                onClick={handlePropose}
                disabled={proposing || !newLang || !newTopic}
                className="px-4 py-2 bg-accent-teal text-black rounded font-medium hover:bg-accent-teal/80 disabled:opacity-50"
              >
                {proposing ? 'Proposing...' : `Propose Dialogs — ${selectedTopicLabel}`}
              </button>
            </div>

            {proposeResult && (
              <p className="mt-2 text-sm text-zinc-400">
                {proposeResult.total_available} available dialogs found. Showing top {proposeResult.proposed.length}.
                {proposeResult.proposed.length < 52 && proposeResult.topic === 'general' && (
                  <span className="text-yellow-400 ml-1">
                    Warning: fewer than 52 dialogs available.
                  </span>
                )}
              </p>
            )}
          </div>

          {proposed && proposed.length > 0 && (
            <>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
                <h3 className="text-white font-medium">
                  Proposed: {proposed.length} dialogs for {langAutonym(newLang)} {newLevel}
                </h3>
                <button
                  onClick={handleCreate}
                  disabled={creating}
                  className="px-4 py-2 bg-green-700 text-white rounded font-medium hover:bg-green-600 disabled:opacity-50"
                >
                  {creating ? 'Saving...' : 'Save Campaign (Draft)'}
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[800px] text-sm">
                  <thead>
                    <tr className="border-b border-zinc-700 text-left text-zinc-400">
                      <th className="py-2 px-3">Week</th>
                      <th className="py-2 px-3">Title</th>
                      <th className="py-2 px-3">Scenario</th>
                      <th className="py-2 px-3">Category</th>
                      <th className="py-2 px-3">Quality</th>
                      <th className="py-2 px-3">Slug</th>
                    </tr>
                  </thead>
                  <tbody>
                    {proposed.map((p) => (
                      <tr key={p.week} className="border-b border-zinc-800">
                        <td className="py-2 px-3 text-white font-mono">W{String(p.week).padStart(2, '0')}</td>
                        <td className="py-2 px-3 text-white">{p.dialog_title}</td>
                        <td className="py-2 px-3 text-zinc-400">{p.scenario_code}</td>
                        <td className="py-2 px-3 text-zinc-400">{p.scenario_category}</td>
                        <td className="py-2 px-3 text-white">{p.quality_score}</td>
                        <td className="py-2 px-3 text-zinc-500 text-xs truncate max-w-[200px]">{p.dialog_slug}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
